import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Home, Calendar, Pencil, Trash2, Dumbbell, ChevronDown, ChevronUp, Save, X, Plus } from 'lucide-react';
import FullScreenWorkoutEditor from '../workout/FullScreenWorkoutEditor';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLE = {
  scheduled: { label: 'Scheduled', cls: 'bg-amber-500/20 text-amber-200 border-amber-400/40' },
  in_progress: { label: 'In progress', cls: 'bg-blue-500/20 text-blue-200 border-blue-400/40' },
  completed: { label: 'Completed', cls: 'bg-green-500/20 text-green-200 border-green-400/40' },
};

const fmtDate = (iso) => {
  if (!iso) return '…';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const ClientFlexibleRoutines = ({ clientId, t }) => {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [editingMeta, setEditingMeta] = useState(null); // routine being edited (dates/notes/name)
  const [metaForm, setMetaForm] = useState({ template_name: '', available_from: '', available_until: '', trainer_notes: '' });
  const [savingMeta, setSavingMeta] = useState(false);
  const [editingExercises, setEditingExercises] = useState(null); // routine whose exercises are edited
  const [allExercises, setAllExercises] = useState([]);
  const [libLoading, setLibLoading] = useState(false);

  const fetchRoutines = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/workouts?client_id=${clientId}&mode=flexible`);
      setRoutines(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (e) {
      setError('Could not load free routines.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchRoutines(); }, [fetchRoutines]);

  const openExerciseEditor = async (routine) => {
    setEditingExercises(routine);
    if (allExercises.length === 0) {
      try {
        setLibLoading(true);
        const res = await axios.get(`${API}/exercises/list`);
        setAllExercises(Array.isArray(res.data) ? res.data : (res.data?.exercises || []));
      } catch (e) {
        setAllExercises([]);
      } finally {
        setLibLoading(false);
      }
    }
  };

  const handleSaveExercises = async (booking, existingWorkout, exercises) => {
    const exerciseData = exercises.map((ex) => ({
      exercise_id: ex.exercise_id || '',
      exercise_name: ex.exercise_name || 'Unknown Exercise',
      sets: parseInt(ex.target_sets || ex.sets) || 4,
      reps: parseInt(ex.target_reps || ex.reps) || 12,
      weight: parseFloat(ex.target_weight || ex.weight) || 0,
      target_sets: parseInt(ex.target_sets || ex.sets) || 4,
      target_reps: parseInt(ex.target_reps || ex.reps) || 12,
      target_weight: parseFloat(ex.target_weight || ex.weight) || 0,
      rest_time: parseInt(ex.rest_time) || 120,
      is_superset: ex.is_superset || false,
      superset_group: ex.is_superset ? (ex.superset_group || 'A') : (ex.superset_group || null),
      actual_sets: ex.actual_sets || [],
      note: ex.note || '',
      notes: ex.notes || '',
      image_url: ex.image_url || '',
      animation_webp: ex.animation_webp || '',
      muscle_groups: ex.muscle_groups || [],
      difficulty: ex.difficulty || '',
      equipment: ex.equipment || '',
    }));
    await axios.put(`${API}/workouts/${existingWorkout.id}/set-exercises`, exerciseData);
    setEditingExercises(null);
    fetchRoutines();
  };

  const startEditMeta = (r) => {
    setEditingMeta(r.id);
    setMetaForm({
      template_name: r.template_name || '',
      available_from: (r.available_from || '').slice(0, 10),
      available_until: (r.available_until || '').slice(0, 10),
      trainer_notes: r.trainer_notes || '',
    });
  };

  const saveMeta = async (id) => {
    try {
      setSavingMeta(true);
      await axios.put(`${API}/workouts/${id}`, {
        template_name: metaForm.template_name || undefined,
        available_from: metaForm.available_from || undefined,
        available_until: metaForm.available_until || undefined,
        trainer_notes: metaForm.trainer_notes || undefined,
      });
      setEditingMeta(null);
      fetchRoutines();
    } catch (e) {
      alert('Could not save changes: ' + (e.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Remove the free routine "${r.template_name || 'Routine'}"?\n\nThis deletes the assignment for this client and cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/workouts/${r.id}`);
      fetchRoutines();
    } catch (e) {
      alert('Could not delete: ' + (e.response?.data?.detail || 'Unknown error'));
    }
  };

  if (loading) return <div className="text-blue-200 py-8 text-center" data-testid="flex-routines-loading">Loading free routines…</div>;
  if (error) return <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4 text-red-200" data-testid="flex-routines-error">{error}</div>;

  return (
    <div className="space-y-4" data-testid="client-flexible-routines">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            <Home className="w-5 h-5 text-amber-300" /> Free Routines
          </h3>
          <p className="text-blue-300 text-sm">Routines this client can perform outside the studio, on their own schedule.</p>
        </div>
        <span className="text-blue-300 text-sm" data-testid="flex-routines-count">{routines.length} assigned</span>
      </div>

      {routines.length === 0 && (
        <div className="glass-card p-8 text-center text-blue-200" data-testid="flex-routines-empty">
          No free routines assigned yet. Assign one from <strong>Workouts → Templates → “Assign as Rutina libre”</strong>.
        </div>
      )}

      {routines.map((r) => {
        const s = STATUS_STYLE[r.status] || STATUS_STYLE.scheduled;
        const isOpen = expanded[r.id];
        const exs = r.exercises || [];
        const todayStr = new Date().toISOString().slice(0, 10);
        const isExpired = r.status !== 'completed' && (r.available_until || '').slice(0, 10) && (r.available_until).slice(0, 10) < todayStr;
        return (
          <div key={r.id} className="glass-card p-4 border-l-4 border-amber-400" data-testid={`flex-routine-${r.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <h4 className="text-white font-semibold truncate">{r.template_name || 'Free routine'}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
                  {isExpired && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full border bg-slate-500/20 text-slate-300 border-slate-400/40"
                      data-testid={`flex-routine-expired-${r.id}`}
                      title="Availability window has ended"
                    >
                      Expired
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-blue-300">
                  <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" /> {exs.length} exercises</span>
                  {(r.available_from || r.available_until) && (
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(r.available_from)} → {fmtDate(r.available_until)}</span>
                  )}
                </div>
                {r.trainer_notes && <p className="text-sm text-blue-200/80 mt-2 italic line-clamp-2">"{r.trainer_notes}"</p>}
              </div>

              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 text-blue-200 text-sm" data-testid={`flex-routine-toggle-${r.id}`}>
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  View
                </button>
                <button onClick={() => startEditMeta(r)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-sm" data-testid={`flex-routine-edit-meta-${r.id}`}>
                  <Pencil className="w-4 h-4" /> Details
                </button>
                <button onClick={() => openExerciseEditor(r)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 text-sm" data-testid={`flex-routine-edit-ex-${r.id}`}>
                  <Dumbbell className="w-4 h-4" /> Exercises
                </button>
                <button onClick={() => handleDelete(r)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm" data-testid={`flex-routine-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Inline meta editor */}
            {editingMeta === r.id && (
              <div className="mt-4 p-4 rounded-lg bg-slate-800/60 border border-slate-600/50 space-y-3" data-testid={`flex-routine-meta-form-${r.id}`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-blue-300 text-xs mb-1">Routine name</label>
                    <input value={metaForm.template_name} onChange={(e) => setMetaForm((f) => ({ ...f, template_name: e.target.value }))} className="input-field text-sm" data-testid={`flex-meta-name-${r.id}`} />
                  </div>
                  <div>
                    <label className="block text-blue-300 text-xs mb-1">Available from</label>
                    <input type="date" value={metaForm.available_from} onChange={(e) => setMetaForm((f) => ({ ...f, available_from: e.target.value }))} className="input-field text-sm" data-testid={`flex-meta-from-${r.id}`} />
                  </div>
                  <div>
                    <label className="block text-blue-300 text-xs mb-1">Available until</label>
                    <input type="date" value={metaForm.available_until} onChange={(e) => setMetaForm((f) => ({ ...f, available_until: e.target.value }))} className="input-field text-sm" data-testid={`flex-meta-until-${r.id}`} />
                  </div>
                </div>
                <div>
                  <label className="block text-blue-300 text-xs mb-1">Trainer notes</label>
                  <textarea value={metaForm.trainer_notes} onChange={(e) => setMetaForm((f) => ({ ...f, trainer_notes: e.target.value }))} rows={2} className="input-field text-sm" data-testid={`flex-meta-notes-${r.id}`} />
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingMeta(null)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-700/50 text-blue-200 text-sm"><X className="w-4 h-4" /> Cancel</button>
                  <button onClick={() => saveMeta(r.id)} disabled={savingMeta} className="flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-500 text-white text-sm disabled:opacity-50" data-testid={`flex-meta-save-${r.id}`}><Save className="w-4 h-4" /> {savingMeta ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            )}

            {/* Expanded exercise list */}
            {isOpen && (
              <div className="mt-4 space-y-2" data-testid={`flex-routine-exlist-${r.id}`}>
                {exs.length === 0 && <p className="text-blue-300 text-sm">No exercises in this routine.</p>}
                {exs.map((ex, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/40">
                    <div className="w-10 h-10 rounded bg-slate-700 overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {(() => {
                        const src = getExerciseAnimationUrl(ex);
                        return src
                          ? <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          : <Dumbbell className="w-4 h-4 text-slate-500" />;
                      })()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{i + 1}. {ex.exercise_name_es || ex.exercise_name}</div>
                      <div className="text-blue-300 text-xs">{ex.target_sets || ex.sets || '-'} × {ex.target_reps || ex.reps || '-'}{ex.equipment ? ` · ${ex.equipment}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Full exercise editor */}
      {editingExercises && (
        libLoading ? (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center text-white">Loading exercise library…</div>
        ) : (
          <FullScreenWorkoutEditor
            booking={null}
            workout={editingExercises}
            mode="plan"
            exercises={allExercises}
            templates={[]}
            clientId={clientId}
            onSave={handleSaveExercises}
            onClose={() => setEditingExercises(null)}
            t={t || ((k) => k)}
          />
        )
      )}
    </div>
  );
};

export default ClientFlexibleRoutines;
